import { Box, CellContentPosition, ConfirmationDialog, Table, TableProps, Typography } from "@tm/components"
import { CustomRepairTimeItemResponse, useDeleteCustomRepairTimes, useShowCustomRepairTimeListItems } from "@tm/data/basket"
import { useLocalization } from "@tm/localization"
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react"
import { useSelection } from "@tm/utils"
import { useAfterPaintEffect } from "@bundles/parts/components/ListV2/helpers"
import { useMoveCustomRepairTimes } from "@tm/data/basket/customRepairTimes/hooks/useMoveCustomRepairTimes"
import { OwnRepairTimeItem } from "./OwnRepairTimeItem"
import { useOwnRepairTimesStore } from "../store"
import { ItemSelection } from "../../../ItemSelection"
import { TableLoading } from "../../../TableLoading"
import { OwnListSearchType } from "../models"
import { useAllCompilations } from "../useAllCompilations"

export type RepairTimeListHandle = {
    scrollToTop(): void
}

type RepairTimesListProps = {
    onAddRepairTimesToCostEstimation(items: CustomRepairTimeItemResponse[]): void
    onEditRepairTime(repairTime: CustomRepairTimeItemResponse): void
}

export const RepairTimesList = forwardRef<RepairTimeListHandle, RepairTimesListProps>((props, ref) => {
    const { onAddRepairTimesToCostEstimation, onEditRepairTime } = props
    const { translateText } = useLocalization()
    const { searchQuery, selectedCompilation, compilations, selectedSearchType } = useOwnRepairTimesStore()
    const { mutateAsync: moveRepairTimes } = useMoveCustomRepairTimes()
    const { mutateAsync: deleteRepairTimes } = useDeleteCustomRepairTimes()

    const tableRef = useRef<HTMLDivElement>(null)
    const autoFetchIndex = useRef(0)

    const listRequest = useMemo<Parameters<typeof useShowCustomRepairTimeListItems>[0]>(
        () => ({
            id: selectedCompilation?.id,
            query: selectedSearchType === OwnListSearchType.Repairtime ? searchQuery : undefined,
        }),
        [searchQuery, selectedCompilation, selectedSearchType]
    )

    const {
        data: { pages } = {},
        fetchNextPage,
        isLoading,
        isFetchingNextPage,
        isFetched,
    } = useShowCustomRepairTimeListItems(listRequest, !!compilations)

    const repairTimes = useMemo(() => pages?.flatMap((x) => x.data) ?? [], [pages])
    const pageIndex = pages?.length ?? 0

    const getAllRepairTimeIds = useCallback(() => repairTimes?.map((repairTime) => repairTime.id) || [], [repairTimes])
    const { selectedIds, toggleSelected, selectAll, unselectAll } = useSelection(getAllRepairTimeIds)
    const [deleteIds, setDeleteIds] = useState<string[]>()

    const allCompilations = useAllCompilations()

    useImperativeHandle(ref, () => {
        return {
            scrollToTop: () => {
                tableRef.current?.scrollTo({ top: 0 })
            },
        }
    })

    const handleAddToBasket = useCallback(
        (repairTime: CustomRepairTimeItemResponse) => {
            onAddRepairTimesToCostEstimation([repairTime])
        },
        [onAddRepairTimesToCostEstimation]
    )

    const handleAddRepairTimesToBasket = useCallback(
        async (ids: string[]) => {
            const list: CustomRepairTimeItemResponse[] = []
            ids.forEach((id) => {
                const art = repairTimes.find((x) => x.id === id)
                if (art) {
                    list.push(art)
                }
            })
            await onAddRepairTimesToCostEstimation(list)
        },
        [repairTimes]
    )

    const handleToggleRepairTime = useCallback(
        (repairTime: CustomRepairTimeItemResponse) => {
            toggleSelected(repairTime.id)
        },
        [selectedIds]
    )

    const handleDeleteRepairTimes = useCallback(async (ids: string[]) => {
        setDeleteIds(ids)
    }, [])

    const handleDeleteRepairTime = useCallback((repairTime: CustomRepairTimeItemResponse) => {
        setDeleteIds([repairTime.id])
    }, [])

    const handleDeleteConfirm = useCallback(async () => {
        if (deleteIds) {
            try {
                await deleteRepairTimes({ ids: deleteIds })
            } finally {
                setDeleteIds(undefined)
                unselectAll()
            }
        }
    }, [repairTimes, deleteIds, deleteRepairTimes])

    const closeDeleteDialog = useCallback(() => {
        setDeleteIds(undefined)
    }, [])

    const handleMoveRepairTimes = useCallback(async (ids: string[], sourceId: string | undefined, targetId: string) => {
        try {
            await moveRepairTimes({ idList: ids, customRepairTimeListSourceId: sourceId, customRepairTimeListTargetId: targetId })
        } finally {
            unselectAll()
        }
    }, [])

    useAfterPaintEffect(() => {
        const scrollContainer = tableRef.current
        if (
            !!repairTimes.length &&
            pageIndex > autoFetchIndex.current &&
            scrollContainer &&
            scrollContainer.clientHeight === scrollContainer.scrollHeight &&
            document.body.clientHeight > scrollContainer.clientHeight // prevent endless loop loading problems
        ) {
            // auto fetch until no new pageIndex comes
            autoFetchIndex.current = pageIndex
            fetchNextPage()
        }
    }, [tableRef.current, repairTimes, pageIndex])

    const tableData: TableProps = {
        scrollbar: "system",
        headerStyle: "default",
        headerBackground: "inherit",
        columns: [
            { header: translateText(58), gridSize: "1fr" }, // Number/id
            { header: translateText(25), gridSize: "2fr" }, // description
            { header: translateText(158), gridSize: "2fr" }, // Category
            { header: translateText(63), gridSize: "1fr", alignContent: CellContentPosition.right }, // Hourly Rate
            { header: translateText(84), gridSize: "1fr", alignContent: CellContentPosition.right }, // Work time
            { header: translateText(57), gridSize: "1fr", alignContent: CellContentPosition.right }, // Price
            { header: translateText(706), gridSize: "1fr", alignContent: CellContentPosition.right, sx: { pr: 3 } }, // Vat
            { alignContent: CellContentPosition.right }, // Actions
        ],
    }

    return (
        <Box minHeight={0} flex={1} position="relative">
            <ItemSelection
                positionType="article"
                selectedItemIds={selectedIds}
                lists={allCompilations}
                selectedListId={selectedCompilation?.id}
                allSelected={repairTimes.every((x) => selectedIds.includes(x.id))}
                onDelete={handleDeleteRepairTimes}
                onSelectAll={selectAll}
                onUnselectAll={unselectAll}
                onAddToBasket={handleAddRepairTimesToBasket}
                onMove={handleMoveRepairTimes}
            />
            {(isLoading || isFetchingNextPage) && <TableLoading colCount={tableData.columns.length} />}
            {isFetched && !repairTimes.length && (
                <Typography sx={{ marginBlock: 1, textAlign: "center", gridColumn: `1 / span ${tableData.columns.length}` }}>
                    {translateText(13716)}
                </Typography>
            )}

            {isFetched && !!repairTimes.length && (
                <Table {...tableData} onScrollBottom={fetchNextPage} ref={tableRef} overflowY="auto">
                    {repairTimes.map((repairTime) => (
                        <OwnRepairTimeItem
                            key={repairTime.id}
                            repairTime={repairTime}
                            isSelected={selectedIds.includes(repairTime.id)}
                            isDeleting={!!deleteIds?.length}
                            onAddToCostEstimation={handleAddToBasket}
                            onDelete={handleDeleteRepairTime}
                            onEdit={onEditRepairTime}
                            onToggle={handleToggleRepairTime}
                        />
                    ))}
                </Table>
            )}
            <ConfirmationDialog
                open={!!deleteIds?.length}
                confirmationButtonText={translateText(585)}
                cancelButtonText={translateText(584)}
                onConfirm={handleDeleteConfirm}
                onCancel={closeDeleteDialog}
            >
                {translateText(833)}
            </ConfirmationDialog>
        </Box>
    )
})
